import { Modal, ModalProps, ModalFuncProps } from "antd";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/shared/hooks/useTheme";
import { AlertTriangle, Trash2 } from "lucide-react";
import CustomButton from "@/shared/ui/button";
import "./CustomModal.scss";

interface CustomModalProps extends Omit<ModalProps, "footer"> {
  "data-cy"?: string;
  variant?: "default" | "danger" | "warning" | "info";
  icon?: React.ReactNode;
  subtitle?: string;
  warningMessage?: React.ReactNode | string;
  footer?: React.ReactNode;
}

const CustomModal = ({
  "data-cy": dataCy = "custom-modal",
  className = "",
  okText,
  cancelText,
  children,
  title,
  variant = "default",
  icon,
  subtitle,
  warningMessage,
  footer,
  onOk,
  onCancel,
  confirmLoading,
  okButtonProps,
  ...props
}: CustomModalProps) => {
  const { theme } = useAppTheme();
  const { t } = useTranslation();

  const modalClassName = `
    custom-modal
    custom-modal-${variant}
    ${theme === "dark" ? "custom-modal-dark" : "custom-modal-light"}
    ${className}
  `.trim();

  // Use i18n translations if not provided
  const modalOkText = okText || t("actions.confirm");
  const modalCancelText = cancelText || t("actions.cancel");

  // Default icons based on variant
  const getDefaultIcon = () => {
    switch (variant) {
      case "danger":
        return <AlertTriangle className="w-6 h-6" />;
      case "warning":
        return <AlertTriangle className="w-6 h-6" />;
      default:
        return null;
    }
  };

  const modalIcon = icon !== undefined ? icon : getDefaultIcon();

  // Custom header with icon
  const customHeader = title ? (
    <div className="custom-modal-header">
      {modalIcon && (
        <div className={`custom-modal-icon custom-modal-icon-${variant}`}>
          {modalIcon}
        </div>
      )}
      <div className="custom-modal-title-wrapper">
        <h3 className="custom-modal-title">{title}</h3>
        {subtitle && <p className="custom-modal-subtitle">{subtitle}</p>}
      </div>
    </div>
  ) : null;

  // Custom footer with CustomButton
  const customFooter =
    footer === null ? null : footer !== undefined ? (
      footer
    ) : (
      <div className="custom-modal-footer">
        <CustomButton
          variant="outline"
          onClick={onCancel as any}
          disabled={confirmLoading}
          data-cy={`${dataCy}-cancel`}
        >
          {modalCancelText}
        </CustomButton>
        {/* `okButtonProps.disabled` and `.loading` are honoured here because this
            footer is hand-built and AntD never sees them — before this, passing
            them did NOTHING, and two call sites had already been bitten:
            SyncCategoriesModal's `disabled: checkedIds.length === 0` (a Sync
            button that stayed clickable with nothing selected) and
            SuspendStoreModal's `danger: true`.

            COLOUR still comes from `variant`, not from `okButtonProps.danger` —
            several existing modals pass `danger` here and honouring it would
            restyle all of them at once, including ones whose `variant` was
            deliberately chosen. `variant="danger"` also attaches a Trash2 icon,
            which is not always what a destructive-looking action means. */}
        <CustomButton
          variant={variant ? variant : "primary"}
          onClick={onOk as any}
          loading={confirmLoading || okButtonProps?.loading}
          disabled={okButtonProps?.disabled}
          icon={
            variant === "danger" ? <Trash2 className="w-4 h-4" /> : undefined
          }
          data-cy={`${dataCy}-ok`}
        >
          {modalOkText}
        </CustomButton>
      </div>
    );

  return (
    <Modal
      {...props}
      className={modalClassName}
      title={null}
      footer={null}
      data-cy={dataCy}
      centered
      closeIcon={null}
      destroyOnHidden
      maskClosable={true}
      onCancel={onCancel}
    >
      {customHeader}
      <div className="custom-modal-content">
        {children}
        {warningMessage && (
          <div className="custom-modal-warning">
            <AlertTriangle className="w-5 h-5" />
            <span>{warningMessage}</span>
          </div>
        )}
      </div>
      {customFooter}
    </Modal>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Static (imperative) API.
//
// These were bare pass-throughs to AntD — `Modal.confirm(config)` and nothing
// else — so every caller that reached for CustomModal.confirm got a plain AntD
// dialog: none of the `.custom-modal` styling, and none of its phone width cap.
// They looked like they were using the design system and weren't.
//
// `withCustomModalChrome` adds the class the rest of the component applies.
// Theme comes from the document rather than a hook because these are called
// from outside React — the axios error handler uses CustomModal.confirm for the
// session-expired dialog, which is exactly why the imperative API has to stay.
//
// What it still CANNOT give you: `data-cy`. ModalFuncProps has no passthrough
// for arbitrary DOM props, so an imperative dialog can't carry an automation
// hook. Prefer the declarative <CustomModal> inside components for that reason.
// ─────────────────────────────────────────────────────────────────────────────
const withCustomModalChrome = (config: ModalFuncProps): ModalFuncProps => {
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  return {
    centered: true,
    ...config,
    className: [
      "custom-modal",
      isDark ? "custom-modal-dark" : "custom-modal-light",
      config.className ?? "",
    ]
      .filter(Boolean)
      .join(" "),
  };
};

CustomModal.confirm = (config: ModalFuncProps) => {
  return Modal.confirm(withCustomModalChrome(config));
};

CustomModal.info = (config: ModalFuncProps) => {
  return Modal.info(withCustomModalChrome(config));
};

CustomModal.success = (config: ModalFuncProps) => {
  return Modal.success(withCustomModalChrome(config));
};

CustomModal.error = (config: ModalFuncProps) => {
  return Modal.error(withCustomModalChrome(config));
};

CustomModal.warning = (config: ModalFuncProps) => {
  return Modal.warning(withCustomModalChrome(config));
};

CustomModal.destroyAll = () => {
  Modal.destroyAll();
};

export default CustomModal;
